import { apiService } from "./api";

export interface Patient {
  id: string;
  name: string;
  nhsNumber: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  address: string;
  gender: string;
  ethnicity: string;
  allergies: Allergy[];
  conditions: Condition[];
  medications: Medication[];
  vitals: Vital[];
  consultations: Consultation[];
  imported?: boolean;
}

interface Allergy {
  id: string;
  patientId: string;
  name: string;
  severity: string;
  reaction: string;
  dateAdded: string;
}

interface Condition {
  id: string;
  patientId: string;
  name: string;
  severity: string;
  dateAdded: string;
  status: "active" | "inactive" | "resolved";
}

interface Medication {
  id: string;
  patientId: string;
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  prescribedBy: string;
}

interface Vital {
  id: string;
  patientId: string;
  type: "heart-rate" | "weight" | "blood-pressure" | "temperature";
  value: string;
  unit: string;
  timestamp: string;
  recordedBy: string;
}

interface Consultation {
  id: string;
  patientId: string;
  date: string;
  time: string;
  pharmacist: string;
  summary: string;
  actions: ConsultationAction[];
  status: "scheduled" | "in-progress" | "completed" | "cancelled";
}

interface ConsultationAction {
  type: "prescription" | "payment" | "appointment" | "referral" | "vaccine";
  description: string;
  amount?: string;
  date?: string;
}

// Mock patient data for demo purposes
const mockPatients: Patient[] = [
  {
    id: "1",
    name: "John Smith",
    nhsNumber: "NHS123456789",
    dateOfBirth: "1985-03-15",
    email: "john.smith@email.com",
    phone: "07700 123456",
    address: "123 Main Street, London, SW1A 1AA",
    gender: "Male",
    ethnicity: "White British",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
  {
    id: "2",
    name: "Sarah Johnson",
    nhsNumber: "NHS987654321",
    dateOfBirth: "1990-07-22",
    email: "sarah.johnson@email.com",
    phone: "07700 987654",
    address: "456 Oak Avenue, Manchester, M1 1AA",
    gender: "Female",
    ethnicity: "White British",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
  {
    id: "3",
    name: "Ahmed Hassan",
    nhsNumber: "NHS456789123",
    dateOfBirth: "1978-12-03",
    email: "ahmed.hassan@email.com",
    phone: "07700 456789",
    address: "789 Cedar Close, Birmingham, B1 1AA",
    gender: "Male",
    ethnicity: "Asian",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
  {
    id: "4",
    name: "Emma Wilson",
    nhsNumber: "NHS789123456",
    dateOfBirth: "1992-05-18",
    email: "emma.wilson@email.com",
    phone: "07700 789123",
    address: "321 Pine Road, Leeds, LS1 1AA",
    gender: "Female",
    ethnicity: "White British",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
  {
    id: "5",
    name: "Michael Brown",
    nhsNumber: "NHS321654987",
    dateOfBirth: "1975-09-30",
    email: "michael.brown@email.com",
    phone: "07700 321654",
    address: "654 Elm Street, Liverpool, L1 1AA",
    gender: "Male",
    ethnicity: "White British",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
  {
    id: "6",
    name: "Test Patient (no contact)",
    nhsNumber: "NHS000000000",
    dateOfBirth: "1999-01-01",
    email: "",
    phone: "",
    address: "No Address Provided",
    gender: "Other",
    ethnicity: "Other",
    allergies: [],
    conditions: [],
    medications: [],
    vitals: [],
    consultations: [],
  },
];

export class PatientService {
  // Get all patients with mock data
  async getPatients(search?: string, limit?: number): Promise<Patient[]> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    let filteredPatients = mockPatients;

    // Filter by search term if provided
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filteredPatients = mockPatients.filter(
        (patient) =>
          patient.name.toLowerCase().includes(searchLower) ||
          patient.address.toLowerCase().includes(searchLower) ||
          patient.nhsNumber.toLowerCase().includes(searchLower) ||
          patient.email.toLowerCase().includes(searchLower),
      );
    }

    // Apply limit if provided
    if (limit && limit > 0) {
      filteredPatients = filteredPatients.slice(0, limit);
    }

    return filteredPatients;
  }

  // Get patient by ID
  async getPatient(id: string): Promise<Patient> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    const patient = mockPatients.find((p) => p.id === id);
    if (!patient) {
      throw new Error(`Patient with ID ${id} not found`);
    }
    return patient;
  }

  // Create new patient (mock implementation)
  async createPatient(patient: Omit<Patient, "id">): Promise<Patient> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    const newPatient: Patient = {
      ...patient,
      id: Date.now().toString(),
    };

    // In a real app, this would save to the database
    // For now, we'll just return the created patient
    return newPatient;
  }

  // Update patient (mock implementation)
  async updatePatient(id: string, patient: Partial<Patient>): Promise<Patient> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    const existingPatient = mockPatients.find((p) => p.id === id);
    if (!existingPatient) {
      throw new Error(`Patient with ID ${id} not found`);
    }

    const updatedPatient: Patient = {
      ...existingPatient,
      ...patient,
      id, // Ensure ID doesn't change
    };

    return updatedPatient;
  }

  // Delete patient (mock implementation)
  async deletePatient(id: string): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    const patientIndex = mockPatients.findIndex((p) => p.id === id);
    if (patientIndex === -1) {
      throw new Error(`Patient with ID ${id} not found`);
    }

    // In a real app, this would delete from the database
    // For now, we'll just simulate success
  }

  // Patient Allergies
  async addAllergy(
    patientId: string,
    allergy: Omit<Allergy, "id" | "patientId">,
  ): Promise<Allergy> {
    return apiService.post<Allergy>(
      `/patients/${patientId}/allergies`,
      allergy,
    );
  }

  async updateAllergy(
    patientId: string,
    allergyId: string,
    allergy: Partial<Allergy>,
  ): Promise<Allergy> {
    return apiService.put<Allergy>(
      `/patients/${patientId}/allergies/${allergyId}`,
      allergy,
    );
  }

  async deleteAllergy(patientId: string, allergyId: string): Promise<void> {
    return apiService.delete<void>(
      `/patients/${patientId}/allergies/${allergyId}`,
    );
  }

  // Patient Conditions
  async addCondition(
    patientId: string,
    condition: Omit<Condition, "id" | "patientId">,
  ): Promise<Condition> {
    return apiService.post<Condition>(
      `/patients/${patientId}/conditions`,
      condition,
    );
  }

  async updateCondition(
    patientId: string,
    conditionId: string,
    condition: Partial<Condition>,
  ): Promise<Condition> {
    return apiService.put<Condition>(
      `/patients/${patientId}/conditions/${conditionId}`,
      condition,
    );
  }

  async deleteCondition(patientId: string, conditionId: string): Promise<void> {
    return apiService.delete<void>(
      `/patients/${patientId}/conditions/${conditionId}`,
    );
  }

  // Patient Medications
  async addMedication(
    patientId: string,
    medication: Omit<Medication, "id" | "patientId">,
  ): Promise<Medication> {
    return apiService.post<Medication>(
      `/patients/${patientId}/medications`,
      medication,
    );
  }

  async updateMedication(
    patientId: string,
    medicationId: string,
    medication: Partial<Medication>,
  ): Promise<Medication> {
    return apiService.put<Medication>(
      `/patients/${patientId}/medications/${medicationId}`,
      medication,
    );
  }

  async deleteMedication(
    patientId: string,
    medicationId: string,
  ): Promise<void> {
    return apiService.delete<void>(
      `/patients/${patientId}/medications/${medicationId}`,
    );
  }

  // Patient Vitals
  async addVital(
    patientId: string,
    vital: Omit<Vital, "id" | "patientId">,
  ): Promise<Vital> {
    return apiService.post<Vital>(`/patients/${patientId}/vitals`, vital);
  }

  async getVitals(patientId: string, type?: string): Promise<Vital[]> {
    const query = type ? `?type=${type}` : "";
    return apiService.get<Vital[]>(`/patients/${patientId}/vitals${query}`);
  }

  async deleteVital(patientId: string, vitalId: string): Promise<void> {
    return apiService.delete<void>(`/patients/${patientId}/vitals/${vitalId}`);
  }

  // Patient Consultations
  async getConsultations(patientId: string): Promise<Consultation[]> {
    return apiService.get<Consultation[]>(
      `/patients/${patientId}/consultations`,
    );
  }

  async createConsultation(
    patientId: string,
    consultation: Omit<Consultation, "id" | "patientId">,
  ): Promise<Consultation> {
    return apiService.post<Consultation>(
      `/patients/${patientId}/consultations`,
      consultation,
    );
  }

  async updateConsultation(
    patientId: string,
    consultationId: string,
    consultation: Partial<Consultation>,
  ): Promise<Consultation> {
    return apiService.put<Consultation>(
      `/patients/${patientId}/consultations/${consultationId}`,
      consultation,
    );
  }
}

export const patientService = new PatientService();
